import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Users, ShoppingBag,
  Package, ArrowUpRight, ArrowDownRight, RefreshCw,
  Store, ChevronRight, Clock, CheckCircle, AlertTriangle,
  XCircle, Pause, Smartphone, Activity, Wallet, AlertCircle,
  Eye, UserPlus, ShieldAlert, Calendar, Zap, CreditCard,
  ShoppingCart, Globe, BarChart3, PieChart as PieChartIcon,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  CartesianGrid
} from 'recharts';
import './Dashboard.css';

const api = async (path, opts = {}) => {
  const response = await fetch(`/api/admin${path}`, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  });
  return response;
};

const formatMoney = (amount, compact = false) => {
  const num = parseFloat(amount) || 0;
  if (compact) {
    if (num >= 1000000000) return `₦${(num / 1000000000).toFixed(1)}B`;
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(0)}K`;
  }
  return `₦${num.toLocaleString()}`;
};

const formatNumber = (num, compact = true) => {
  const n = parseInt(num) || 0;
  if (compact) {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  }
  return n.toLocaleString();
};

const formatPercent = (num) => `${(parseFloat(num) || 0).toFixed(1)}%`;

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const PERIOD_MAP = {
  '24h': 'today',
  '7d': 'last7days',
  '30d': 'last30days',
  '90d': 'last90days'
};

const PERIOD_LABELS = {
  '24h': 'vs yesterday',
  '7d': 'vs last week',
  '30d': 'vs last month',
  '90d': 'vs last quarter'
};

const COLORS = {
  teal: '#0d9488',
  tealLight: '#14b8a6',
  blue: '#3b82f6',
  blueLight: '#60a5fa',
  green: '#10b981',
  greenLight: '#34d399',
  purple: '#8b5cf6',
  purpleLight: '#a78bfa',
  orange: '#f59e0b',
  orangeLight: '#fbbf24',
  red: '#ef4444',
  redLight: '#f87171',
  pink: '#ec4899',
  indigo: '#6366f1',
  cyan: '#06b6d4',
  slate: '#64748b'
};

const CHART_COLORS = [COLORS.teal, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.green, COLORS.pink, COLORS.indigo];

// Growth indicator component
const GrowthIndicator = ({ current, previous, suffix = '' }) => {
  if (!previous || previous === 0) return <span className="dash-growth dash-growth-neutral"><Minus size={12} /> --</span>;

  const change = ((current - previous) / previous) * 100;
  const isPositive = change > 0;
  const isNeutral = Math.abs(change) < 0.5;

  if (isNeutral) {
    return <span className="dash-growth dash-growth-neutral"><Minus size={12} /> 0%{suffix}</span>;
  }

  return (
    <span className={`dash-growth ${isPositive ? 'dash-growth-up' : 'dash-growth-down'}`}>
      {isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
      {Math.abs(change).toFixed(1)}%{suffix}
    </span>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7d');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Data states
  const [overview, setOverview] = useState({});
  const [previousOverview, setPreviousOverview] = useState({});
  const [stats, setStats] = useState({});
  const [merchants, setMerchants] = useState([]);
  const [settlements, setSettlements] = useState({});
  const [revenueData, setRevenueData] = useState({});
  const [topMerchants, setTopMerchants] = useState([]);
  const [posStats, setPosStats] = useState({});
  const [disputes, setDisputes] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const period = PERIOD_MAP[timeRange] || 'last7days';

      // Calculate previous period for comparison
      const previousPeriod = timeRange === '24h' ? 'yesterday' :
                            timeRange === '7d' ? 'previousWeek' :
                            timeRange === '30d' ? 'previousMonth' : 'previousQuarter';

      const [
        overviewRes,
        prevOverviewRes,
        statsRes,
        merchantsRes,
        settlementsRes,
        revenueRes,
        merchantAnalyticsRes,
        posRes,
        disputesRes,
        ordersRes
      ] = await Promise.allSettled([
        api(`/analytics/overview?period=${period}`),
        api(`/analytics/overview?period=${previousPeriod}`),
        api('/stats'),
        api('/merchants?limit=1000'),
        api('/settlements/stats'),
        api(`/analytics/revenue?period=${period}`),
        api(`/analytics/merchants?limit=10&sort=revenue&period=${period}`),
        api('/pos/summary'),
        api('/disputes?limit=100'),
        api('/orders?limit=10&sort=createdAt&order=desc')
      ]);

      // Process current overview
      if (overviewRes.status === 'fulfilled' && overviewRes.value.ok) {
        const json = await overviewRes.value.json();
        setOverview(json.data?.summary || json.summary || json || {});
      }

      // Process previous overview for comparison
      if (prevOverviewRes.status === 'fulfilled' && prevOverviewRes.value.ok) {
        const json = await prevOverviewRes.value.json();
        setPreviousOverview(json.data?.summary || json.summary || json || {});
      }

      // Process stats
      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const json = await statsRes.value.json();
        setStats(json.stats || json || {});
      }

      // Process merchants
      if (merchantsRes.status === 'fulfilled' && merchantsRes.value.ok) {
        const json = await merchantsRes.value.json();
        const merchantList = json.merchants || [];
        setMerchants(merchantList);
        // Get recent signups (last 5)
        const sorted = [...merchantList].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setRecentSignups(sorted.slice(0, 5));
      }

      // Process settlements
      if (settlementsRes.status === 'fulfilled' && settlementsRes.value.ok) {
        const json = await settlementsRes.value.json();
        setSettlements(json || {});
      }

      // Process revenue data
      if (revenueRes.status === 'fulfilled' && revenueRes.value.ok) {
        const json = await revenueRes.value.json();
        setRevenueData(json.data || json || {});
      }

      // Process top merchants
      if (merchantAnalyticsRes.status === 'fulfilled' && merchantAnalyticsRes.value.ok) {
        const json = await merchantAnalyticsRes.value.json();
        setTopMerchants(json.data?.merchants || json.merchants || json.topMerchants || []);
      }

      // Process POS stats
      if (posRes.status === 'fulfilled' && posRes.value.ok) {
        const json = await posRes.value.json();
        setPosStats(json.data || json || {});
      }

      // Process disputes
      if (disputesRes.status === 'fulfilled' && disputesRes.value.ok) {
        const json = await disputesRes.value.json();
        setDisputes(json.disputes || json.data?.disputes || []);
      }

      // Process recent orders
      if (ordersRes.status === 'fulfilled' && ordersRes.value.ok) {
        const json = await ordersRes.value.json();
        setRecentOrders(json.orders || json.data?.orders || []);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Computed metrics - Current
  const totalMerchants = stats.totalMerchants || overview.totalMerchants || merchants.length;
  const totalCustomers = stats.totalCustomers || overview.totalCustomers || 0;
  const totalOrders = overview.totalOrders || stats.totalOrders || 0;
  const completedOrders = overview.completedOrders || 0;
  const totalRevenue = overview.totalRevenue || stats.totalRevenue || 0;
  const platformRevenue = overview.platformRevenue || 0;
  const avgOrderValue = overview.avgOrderValue || 0;
  const conversionRate = overview.conversionRate || 0;
  const activeCustomers = overview.activeCustomers || 0;
  const activeMerchants = overview.activeMerchants || 0;
  const totalProducts = stats.totalProducts || 0;

  // Previous period metrics
  const prevTotalOrders = previousOverview.totalOrders || 0;
  const prevTotalRevenue = previousOverview.totalRevenue || 0;
  const prevPlatformRevenue = previousOverview.platformRevenue || 0;
  const prevAvgOrderValue = previousOverview.avgOrderValue || 0;
  const prevActiveCustomers = previousOverview.activeCustomers || 0;
  const prevActiveMerchants = previousOverview.activeMerchants || 0;

  // Revenue data
  const gmv = revenueData.summary?.totalGMV || totalRevenue;
  const serviceChargeIncome = revenueData.summary?.platformRevenue || platformRevenue;

  // Settlement stats
  const onHoldAmount = parseFloat(settlements.onHold?._sum?.merchantAmount || settlements.onHold?.amount || 0);
  const onHoldCount = settlements.onHold?._count || settlements.onHold?.count || 0;
  const pendingAmount = parseFloat(settlements.pending?._sum?.merchantAmount || settlements.pending?.amount || 0);
  const pendingCount = settlements.pending?._count || settlements.pending?.count || 0;
  const completedAmount = parseFloat(settlements.completed?._sum?.merchantAmount || settlements.completed?.amount || 0);
  const completedCount = settlements.completed?._count || settlements.completed?.count || 0;

  // POS stats
  const posTotalOrders = posStats.totalOrders || 0;
  const posTotalGMV = posStats.totalGMV || 0;

  // Merchant status breakdown
  const merchantsByStatus = {
    approved: merchants.filter(m => m.store?.applicationStatus === 'APPROVED').length,
    pending: merchants.filter(m => m.store?.applicationStatus === 'PENDING' || m.store?.applicationStatus === 'PENDING_REVIEW').length,
    suspended: merchants.filter(m => m.store?.isSuspended).length,
    banned: merchants.filter(m => m.store?.isBanned).length
  };

  // Merchant tier breakdown
  const tierCounts = merchants.reduce((acc, m) => {
    const tier = m.merchantTier || m.tier || 'FREE';
    acc[tier] = (acc[tier] || 0) + 1;
    return acc;
  }, {});

  const tierData = Object.entries(tierCounts).map(([name, value], i) => ({
    name: name.charAt(0) + name.slice(1).toLowerCase(),
    value,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));

  // Order channel breakdown
  const storefrontOrders = totalOrders - posTotalOrders;
  const channelData = [
    { name: 'Storefront', value: storefrontOrders > 0 ? storefrontOrders : 0, color: COLORS.blue },
    { name: 'POS', value: posTotalOrders, color: COLORS.green },
  ].filter(c => c.value > 0);

  // Order status breakdown
  const orderStatusData = [
    { name: 'Delivered', value: completedOrders, color: COLORS.green },
    { name: 'Processing', value: Math.max(0, totalOrders - completedOrders), color: COLORS.orange },
  ].filter(c => c.value > 0);

  // Active disputes
  const activeDisputes = disputes.filter(d => d.status === 'PENDING' || d.status === 'OPEN');
  const pendingApprovals = merchants.filter(m =>
    m.store?.applicationStatus === 'PENDING' || m.store?.applicationStatus === 'PENDING_REVIEW'
  );

  // Revenue chart data
  const dailyGMV = revenueData.dailyGMV || [];
  const revenueChartData = dailyGMV.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    gmv: parseFloat(d.gmv) || 0,
    orders: d.orderCount || 0,
    avg: d.orderCount > 0 ? (parseFloat(d.gmv) || 0) / d.orderCount : 0
  }));

  // Merchant growth simulation (based on createdAt)
  const merchantGrowthData = (() => {
    const last30Days = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = merchants.filter(m => {
        const created = new Date(m.createdAt).toISOString().split('T')[0];
        return created <= dateStr;
      }).length;
      last30Days.push({
        date: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        merchants: count
      });
    }
    return last30Days;
  })();

  // Settlement breakdown for bar chart
  const settlementChartData = [
    { name: 'On Hold', amount: onHoldAmount, count: onHoldCount, color: COLORS.orange },
    { name: 'Pending', amount: pendingAmount, count: pendingCount, color: COLORS.blue },
    { name: 'Completed', amount: completedAmount, count: completedCount, color: COLORS.green },
  ];

  // Alerts
  const alerts = [];
  if (pendingApprovals.length > 0) {
    alerts.push({ type: 'warning', icon: UserPlus, message: `${pendingApprovals.length} merchant${pendingApprovals.length > 1 ? 's' : ''} awaiting approval`, link: '/merchants?status=pending', count: pendingApprovals.length });
  }
  if (activeDisputes.length > 0) {
    alerts.push({ type: 'danger', icon: ShieldAlert, message: `${activeDisputes.length} active dispute${activeDisputes.length > 1 ? 's' : ''} need attention`, link: '/disputes', count: activeDisputes.length });
  }
  if (onHoldCount > 0) {
    alerts.push({ type: 'info', icon: Clock, message: `${onHoldCount} settlement${onHoldCount > 1 ? 's' : ''} on hold (${formatMoney(onHoldAmount, true)})`, link: '/settlements?status=on_hold', count: onHoldCount });
  }

  if (loading) {
    return (
      <div className="dash">
        <div className="dash-loading">
          <div className="dash-loader"></div>
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* Header */}
      <header className="dash-header">
        <div className="dash-header-left">
          <h1>{getGreeting()}</h1>
          <p className="dash-subtitle">Platform overview and analytics</p>
        </div>
        <div className="dash-header-right">
          <div className="dash-time-pills">
            {['24h', '7d', '30d', '90d'].map(range => (
              <button
                key={range}
                className={`dash-pill ${timeRange === range ? 'active' : ''}`}
                onClick={() => setTimeRange(range)}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
          <button className="dash-refresh" onClick={fetchDashboardData} title="Refresh">
            <RefreshCw size={16} />
          </button>
          {lastUpdated && (
            <span className="dash-updated">
              {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </header>

      {error && (
        <div className="dash-error">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button onClick={fetchDashboardData}>Retry</button>
        </div>
      )}

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <section className="dash-alerts">
          {alerts.map((alert, i) => (
            <a key={i} href={alert.link} className={`dash-alert dash-alert-${alert.type}`}>
              <alert.icon size={18} />
              <span>{alert.message}</span>
              <ChevronRight size={16} />
            </a>
          ))}
        </section>
      )}

      {/* KPI Cards with Growth */}
      <section className="dash-kpi-row">
        <div className="dash-kpi dash-kpi-primary">
          <div className="dash-kpi-header">
            <div className="dash-kpi-icon"><DollarSign size={20} /></div>
            <GrowthIndicator current={gmv} previous={prevTotalRevenue} suffix={` ${PERIOD_LABELS[timeRange]}`} />
          </div>
          <div className="dash-kpi-body">
            <span className="dash-kpi-label">Gross Merchandise Value</span>
            <h2 className="dash-kpi-value">{formatMoney(gmv)}</h2>
          </div>
          <div className="dash-kpi-footer">
            <span><strong>{formatNumber(totalOrders)}</strong> orders</span>
            <span><strong>{formatMoney(avgOrderValue, true)}</strong> avg</span>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-header">
            <div className="dash-kpi-icon dash-kpi-icon-green"><TrendingUp size={18} /></div>
            <GrowthIndicator current={serviceChargeIncome} previous={prevPlatformRevenue} />
          </div>
          <div className="dash-kpi-body">
            <span className="dash-kpi-label">Platform Revenue</span>
            <h3 className="dash-kpi-value">{formatMoney(serviceChargeIncome)}</h3>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-header">
            <div className="dash-kpi-icon dash-kpi-icon-blue"><ShoppingBag size={18} /></div>
            <GrowthIndicator current={totalOrders} previous={prevTotalOrders} />
          </div>
          <div className="dash-kpi-body">
            <span className="dash-kpi-label">Total Orders</span>
            <h3 className="dash-kpi-value">{formatNumber(totalOrders)}</h3>
          </div>
          <div className="dash-kpi-mini">
            <CheckCircle size={12} className="text-green" /> {completedOrders} delivered
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-header">
            <div className="dash-kpi-icon dash-kpi-icon-purple"><Activity size={18} /></div>
          </div>
          <div className="dash-kpi-body">
            <span className="dash-kpi-label">Conversion Rate</span>
            <h3 className="dash-kpi-value">{formatPercent(conversionRate)}</h3>
          </div>
          <div className="dash-kpi-mini">
            <span className={conversionRate >= 50 ? 'text-green' : 'text-orange'}>
              {conversionRate >= 50 ? 'Healthy' : 'Needs work'}
            </span>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-header">
            <div className="dash-kpi-icon dash-kpi-icon-orange"><Store size={18} /></div>
            <GrowthIndicator current={activeMerchants} previous={prevActiveMerchants} />
          </div>
          <div className="dash-kpi-body">
            <span className="dash-kpi-label">Active Merchants</span>
            <h3 className="dash-kpi-value">{formatNumber(activeMerchants)}</h3>
          </div>
          <div className="dash-kpi-mini">
            <span className="text-muted">{formatNumber(totalMerchants)} total</span>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-header">
            <div className="dash-kpi-icon dash-kpi-icon-cyan"><Users size={18} /></div>
            <GrowthIndicator current={activeCustomers} previous={prevActiveCustomers} />
          </div>
          <div className="dash-kpi-body">
            <span className="dash-kpi-label">Active Customers</span>
            <h3 className="dash-kpi-value">{formatNumber(activeCustomers)}</h3>
          </div>
          <div className="dash-kpi-mini">
            <span className="text-muted">{formatNumber(totalCustomers)} total</span>
          </div>
        </div>
      </section>

      {/* Charts Row 1 - Revenue & Orders */}
      <section className="dash-charts-row">
        <div className="dash-chart dash-chart-lg">
          <div className="dash-chart-header">
            <div>
              <h3>Revenue Trend</h3>
              <p>Daily GMV and order volume</p>
            </div>
            <div className="dash-chart-legend">
              <span><span className="dot" style={{ background: COLORS.teal }}></span>GMV</span>
              <span><span className="dot" style={{ background: COLORS.blue }}></span>Orders</span>
            </div>
          </div>
          <div className="dash-chart-body">
            {revenueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={revenueChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.teal} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={COLORS.teal} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => formatMoney(v, true)} />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: 'none', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                    formatter={(value, name) => [name === 'gmv' ? formatMoney(value) : value, name === 'gmv' ? 'GMV' : 'Orders']}
                  />
                  <Area yAxisId="left" type="monotone" dataKey="gmv" stroke={COLORS.teal} strokeWidth={2} fill="url(#gmvGrad)" />
                  <Bar yAxisId="right" dataKey="orders" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={20} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="dash-chart-empty">No data for selected period</div>
            )}
          </div>
        </div>

        <div className="dash-chart">
          <div className="dash-chart-header">
            <div>
              <h3>Order Channels</h3>
              <p>Sales distribution</p>
            </div>
          </div>
          <div className="dash-chart-body">
            {channelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={channelData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    dataKey="value"
                    labelLine={false}
                  >
                    {channelData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatNumber(value), 'Orders']} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value, entry) => (
                      <span style={{ color: '#64748b', fontSize: 12 }}>
                        {value}: {formatNumber(entry.payload.value)}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="dash-chart-empty">No order data</div>
            )}
          </div>
        </div>
      </section>

      {/* Charts Row 2 - Merchants & Settlements */}
      <section className="dash-charts-row">
        <div className="dash-chart">
          <div className="dash-chart-header">
            <div>
              <h3>Merchant Growth</h3>
              <p>Last 30 days</p>
            </div>
            <span className="dash-chart-total">{formatNumber(totalMerchants)} total</span>
          </div>
          <div className="dash-chart-body">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={merchantGrowthData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="merchantGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.purple} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.purple} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#fff', border: 'none', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="merchants" stroke={COLORS.purple} strokeWidth={2} fill="url(#merchantGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dash-chart">
          <div className="dash-chart-header">
            <div>
              <h3>Merchant Tiers</h3>
              <p>Subscription breakdown</p>
            </div>
          </div>
          <div className="dash-chart-body">
            {tierData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={70}
                    dataKey="value"
                  >
                    {tierData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Merchants']} />
                  <Legend verticalAlign="bottom" height={36} formatter={(value) => <span style={{ color: '#64748b', fontSize: 11 }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="dash-chart-empty">No tier data</div>
            )}
          </div>
        </div>

        <div className="dash-chart">
          <div className="dash-chart-header">
            <div>
              <h3>Settlement Status</h3>
              <p>Amount breakdown</p>
            </div>
            <a href="/settlements" className="dash-chart-link">View all</a>
          </div>
          <div className="dash-chart-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={settlementChartData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => formatMoney(v, true)} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} width={70} />
                <Tooltip formatter={(value) => [formatMoney(value), 'Amount']} contentStyle={{ background: '#fff', border: 'none', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="amount" radius={[0, 6, 6, 0]} barSize={24}>
                  {settlementChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Status Cards */}
      <section className="dash-status-row">
        <div className="dash-status-card">
          <div className="dash-status-header">
            <Store size={18} />
            <h4>Merchant Status</h4>
            <a href="/merchants" className="dash-status-link">View all</a>
          </div>
          <div className="dash-status-grid">
            <div className="dash-status-item">
              <CheckCircle size={16} className="text-green" />
              <span className="dash-status-num">{merchantsByStatus.approved}</span>
              <span className="dash-status-label">Approved</span>
            </div>
            <div className="dash-status-item">
              <Clock size={16} className="text-orange" />
              <span className="dash-status-num">{merchantsByStatus.pending}</span>
              <span className="dash-status-label">Pending</span>
            </div>
            <div className="dash-status-item">
              <Pause size={16} className="text-red" />
              <span className="dash-status-num">{merchantsByStatus.suspended}</span>
              <span className="dash-status-label">Suspended</span>
            </div>
            <div className="dash-status-item">
              <XCircle size={16} className="text-muted" />
              <span className="dash-status-num">{merchantsByStatus.banned}</span>
              <span className="dash-status-label">Banned</span>
            </div>
          </div>
        </div>

        <div className="dash-status-card">
          <div className="dash-status-header">
            <BarChart3 size={18} />
            <h4>Platform Stats</h4>
          </div>
          <div className="dash-status-grid">
            <div className="dash-status-item">
              <Package size={16} className="text-purple" />
              <span className="dash-status-num">{formatNumber(totalProducts)}</span>
              <span className="dash-status-label">Products</span>
            </div>
            <div className="dash-status-item">
              <Smartphone size={16} className="text-green" />
              <span className="dash-status-num">{formatNumber(posTotalOrders)}</span>
              <span className="dash-status-label">POS Orders</span>
            </div>
            <div className="dash-status-item">
              <CreditCard size={16} className="text-teal" />
              <span className="dash-status-num">{formatMoney(posTotalGMV, true)}</span>
              <span className="dash-status-label">POS GMV</span>
            </div>
            <div className="dash-status-item">
              <AlertTriangle size={16} className="text-red" />
              <span className="dash-status-num">{activeDisputes.length}</span>
              <span className="dash-status-label">Disputes</span>
            </div>
          </div>
        </div>

        <div className="dash-status-card">
          <div className="dash-status-header">
            <Wallet size={18} />
            <h4>Settlements</h4>
            <a href="/settlements" className="dash-status-link">Manage</a>
          </div>
          <div className="dash-settlement-list">
            <div className="dash-settlement-row dash-settlement-hold">
              <Clock size={16} />
              <span className="dash-settlement-text">{onHoldCount} On Hold</span>
              <span className="dash-settlement-amt">{formatMoney(onHoldAmount, true)}</span>
            </div>
            <div className="dash-settlement-row dash-settlement-pending">
              <AlertCircle size={16} />
              <span className="dash-settlement-text">{pendingCount} Pending</span>
              <span className="dash-settlement-amt">{formatMoney(pendingAmount, true)}</span>
            </div>
            <div className="dash-settlement-row dash-settlement-done">
              <CheckCircle size={16} />
              <span className="dash-settlement-text">{completedCount} Completed</span>
              <span className="dash-settlement-amt">{formatMoney(completedAmount, true)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lists Row */}
      <section className="dash-lists-row">
        <div className="dash-list">
          <div className="dash-list-header">
            <h3>Top Merchants by Revenue</h3>
            <a href="/merchants" className="dash-list-link">View all <ChevronRight size={14} /></a>
          </div>
          <div className="dash-list-body">
            {topMerchants.length > 0 ? topMerchants.slice(0, 5).map((m, i) => (
              <div key={m.merchantId || i} className="dash-list-row">
                <span className="dash-list-rank">{i + 1}</span>
                <div className="dash-list-info">
                  <span className="dash-list-name">{m.storeName || m.email}</span>
                  <span className="dash-list-meta">{m.orderCount || 0} orders</span>
                </div>
                <span className="dash-list-value">{formatMoney(m.revenue || 0, true)}</span>
              </div>
            )) : (
              <div className="dash-list-empty">No merchant data</div>
            )}
          </div>
        </div>

        <div className="dash-list">
          <div className="dash-list-header">
            <h3>Recent Signups</h3>
            <a href="/merchants" className="dash-list-link">View all <ChevronRight size={14} /></a>
          </div>
          <div className="dash-list-body">
            {recentSignups.length > 0 ? recentSignups.map((m, i) => (
              <div key={m.id || i} className="dash-list-row">
                <div className="dash-list-avatar">
                  <UserPlus size={14} />
                </div>
                <div className="dash-list-info">
                  <span className="dash-list-name">{m.store?.name || m.email}</span>
                  <span className="dash-list-meta">{m.merchantTier || 'FREE'}</span>
                </div>
                <span className="dash-list-date">
                  {new Date(m.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            )) : (
              <div className="dash-list-empty">No recent signups</div>
            )}
          </div>
        </div>

        <div className="dash-list">
          <div className="dash-list-header">
            <h3>Pending Approvals</h3>
            <a href="/merchants?status=pending" className="dash-list-link">Review all <ChevronRight size={14} /></a>
          </div>
          <div className="dash-list-body">
            {pendingApprovals.length > 0 ? pendingApprovals.slice(0, 5).map((m, i) => (
              <div key={m.id || i} className="dash-list-row">
                <div className="dash-list-avatar dash-list-avatar-warning">
                  <Clock size={14} />
                </div>
                <div className="dash-list-info">
                  <span className="dash-list-name">{m.store?.name || m.email}</span>
                  <span className="dash-list-meta">
                    {new Date(m.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <a href={`/merchants/${m.id}`} className="dash-list-action">Review</a>
              </div>
            )) : (
              <div className="dash-list-empty">
                <CheckCircle size={20} className="text-green" />
                <span>All caught up!</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
